import { PalmettoIcon } from '@palmetto/dsm-components';
import { useEffect, useState } from 'react';

import Logo from '../../assets/app-logo-dark.svg';
import { getAppDownloadLink, PlatformConfig } from '../../config';
import { useTranslation, Button } from '@helios/frontend-core';


interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

interface Props {
  showSmartBanner?: boolean;
}

const COOKIE_NAME = 'hideAppBanner';
const COOKIE_MAX_AGE_DAYS = 30;

const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
};

const setCookie = (name: string, value: string, days: number): void => {
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
};

const isAndroid = (): boolean => Boolean(navigator?.userAgent?.match(/Android/i));

// iOS uses the native Safari Smart App Banner (meta tag in index.html).
// This component handles Android web with a direct app-store CTA.
const SmartBannerAppDownload = ({ showSmartBanner }: Props) => {
  const [dismissed, setDismissed] = useState(() => getCookie(COOKIE_NAME) === 'true');
  const { t } = useTranslation();
  const localePrefix = 'smartBanner';

  useEffect(() => {
    if (dismissed || !isAndroid()) return;

    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent): void => {
      // Prefer the explicit Play Store CTA over the browser's install prompt.
      e.preventDefault();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, [dismissed]);

  const shouldShow = !dismissed && !PlatformConfig.isNative && (showSmartBanner || isAndroid());

  const handleDismiss = (): void => {
    setCookie(COOKIE_NAME, 'true', COOKIE_MAX_AGE_DAYS);
    setDismissed(true);
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <div data-theme="core" className="w-full bg-canvas-default -order-1 border-b-2 border-border-default">
      <div className="flex items-center justify-between px-8 py-8">
        <div className="flex items-center">
          <img src={Logo} alt="Palmetto Logo" className="w-[36px]" />
          <div className="flex flex-col pt-4 pl-8 gap-2">
            <span className="label-200 text-text-primary">{t(`${localePrefix}.title`)}</span>
            <span className="body-100 text-text-primary">{t(`${localePrefix}.subtitle`)}</span>
          </div>
        </div>
        <div className="flex items-center">
          <Button
            trackingId="smart-banner-app-download"
            layout="hug"
            size="small"
            emphasis="primary"
            as="a"
            href={getAppDownloadLink()}
            target="_blank"
          >
            {t(`${localePrefix}.view`)}
          </Button>
          <Button
            onClick={handleDismiss}
            layout="hug"
            aria-label="close"
            trackingId="smart-banner-app-download-close"
            emphasis="tertiary"
          >
            <PalmettoIcon name="x" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SmartBannerAppDownload;
